import { Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "./components/layout/Layout";
import { HomePage } from "./pages/HomePage";
import { PhotosPage } from "./pages/PhotosPage";
import { VideosPage } from "./pages/VideosPage";
import { CollectionsPage } from "./pages/CollectionsPage";
import { CollectionPage } from "./pages/CollectionPage";
import { BlogPage } from "./pages/BlogPage";
import { BlogPostPage } from "./pages/BlogPostPage";
import { DocumentPage } from "./pages/DocumentPage";
import { AboutPage } from "./pages/AboutPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { SearchPage } from "./pages/SearchPage";
import { ContactPage } from "./pages/ContactPage";
import { NewsPage } from "./pages/NewsPage";
import { CvPage } from "./pages/CvPage";
import { SignInPage } from "./pages/SignInPage";
import { AccountPage } from "./pages/AccountPage";
import { SessionProvider } from "./session/SessionContext";
import { NotFoundPage } from "./pages/NotFoundPage";
import { AdminAuthProvider, useAdminAuth } from "./admin/AdminAuthContext";
import { AdminLayout } from "./admin/AdminLayout";
import { AdminLogin } from "./admin/AdminLogin";
import { AdminPhotos } from "./admin/AdminPhotos";
import { AdminVideos } from "./admin/AdminVideos";
import { AdminPosts } from "./admin/AdminPosts";
import { AdminDocuments } from "./admin/AdminDocuments";
import { AdminComments } from "./admin/AdminComments";
import { AdminFrontPage } from "./admin/AdminFrontPage";
import { AdminMembers } from "./admin/AdminMembers";

/**
 * Gate for the admin area: show a spinner while a stored key is validated, the
 * sign-in form when signed out, and the admin console (with its nested table
 * routes rendering into its Outlet) when signed in.
 */
function AdminGate() {
  const { status } = useAdminAuth();
  if (status === "checking") {
    return (
      <div className="admin">
        <p className="admin-empty">Checking sign-in…</p>
      </div>
    );
  }
  if (status === "anon") return <AdminLogin />;
  return <AdminLayout />;
}

/**
 * Route table. Every page renders inside the shared Layout (header + left
 * sidebar + footer + scroll-to-top).
 *
 *  - /collections           index of the shelves the library is organised into
 *  - /collections/:id       one collection's own section (any content type)
 *  - /documents/:contentId  canonical detail page for ANY item, hosting comments
 *  - /blog/:slug            friendly URL for journal entries (same view)
 *  - /signin, /account      member sign-in (for photos/videos that are not public)
 *  - /admin/*               admin console (key-gated); CRUD + moderation + re-index
 */
export function App() {
  return (
    <SessionProvider>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="photos" element={<PhotosPage />} />
          <Route path="videos" element={<VideosPage />} />
          <Route path="collections" element={<CollectionsPage />} />
          <Route path="collections/:id" element={<CollectionPage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/:slug" element={<BlogPostPage />} />
          <Route path="documents/:contentId" element={<DocumentPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="news" element={<NewsPage />} />
          <Route path="cv" element={<CvPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="signin" element={<SignInPage />} />
          <Route path="account" element={<AccountPage />} />

          <Route
            path="admin"
            element={
              <AdminAuthProvider>
                <AdminGate />
              </AdminAuthProvider>
            }
          >
            <Route index element={<Navigate to="/admin/photos" replace />} />
            <Route path="photos" element={<AdminPhotos />} />
            <Route path="videos" element={<AdminVideos />} />
            <Route path="blog" element={<AdminPosts />} />
            <Route path="documents" element={<AdminDocuments />} />
            <Route path="comments" element={<AdminComments />} />
            <Route path="front-page" element={<AdminFrontPage />} />
            <Route path="members" element={<AdminMembers />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </SessionProvider>
  );
}
